import { toast } from 'sonner';
import i18n from '../i18n';

/**
 * Shows a toast with an Undo button.
 *
 * Two ways to use it:
 *
 *   1. Already-deleted:  toastUndo(msg, () => restore())
 *      The delete has happened; Undo puts it back.
 *
 *   2. Delayed commit:   toastUndo(msg, () => cancel(), { onExpire: () => commit() })
 *      Nothing has been deleted server-side yet. The item is hidden from the UI,
 *      and the real delete only fires via `onExpire` once the undo window closes.
 *      This gives a true undo without needing a restore endpoint.
 *
 * `onExpire` is guaranteed to run at most once, and never after the user has
 * tapped Undo — otherwise a delayed-commit delete would fire anyway and silently
 * destroy the thing the user just rescued.
 */
export function toastUndo(message, onUndo, opts = {}) {
  const { duration = 6000, description, onExpire } = opts;

  let settled = false;

  const undo = () => {
    if (settled) return;
    settled = true;
    try { onUndo(); } catch (_) { /* never let the toast handler throw */ }
  };

  const expire = () => {
    if (settled) return;
    settled = true;
    if (typeof onExpire === 'function') {
      try { onExpire(); } catch (_) { /* commit is best-effort */ }
    }
  };

  return toast(message, {
    description,
    duration,
    action: {
      label: i18n.t('undo.action', 'Undo'),
      onClick: undo,
    },
    onAutoClose: expire,
    onDismiss: expire,
  });
}
